import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import { PessoaJuridicaFormPart } from "../../shared/components/formulario/parts/PessoaJuridicaFormPart/pessoa-juridica.part";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import { ContaBancariaFormPart } from "../../shared/components/formulario/parts/ContaBancariaFormPart/contaBancariaform.part";
import { SelectObra } from "../../shared/components/formulario/input-select-obra/selectObra";
import { SelectCargo } from "../../shared/components/formulario/input-select-cargo/selectCargo";
import InputData from "../../shared/components/formulario/input-data/inputData";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";

import { mascaraTelefone } from "../../shared/utils/mascaras";
import {
  limpaCamposPsFisica,
  limpaCamposPsJuridica,
} from "../../shared/utils/limpa-campo-form/limpa-campos.part";
import { formatDate } from "../../shared/utils";
import { toastError } from "../../shared/services/errors/erros-handler";
import useLoadingStore from "../../shared/store/loading.store";
import { funcionarioService } from "./service/funcionario.service";

import {
  EditarFuncionarioFormData,
  editarFuncionarioSchema,
} from "./schema/editar-funcionario.schema";
import {
  SectorEnum,
  TypeBondEnum,
} from "../../shared/utils/enum/enum";
import { useMutation } from "@tanstack/react-query";
import InputSetores from "../../shared/components/formulario/inputSetores/inputSetor";
import { useSectorById } from "../../shared/components/formulario/inputSetores/use-sector";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import { SelectUnitOfMeasure, UnitOfMeasureEnum } from "../../shared/components/formulario/select/SelectUnitOfMeasure";
import { SelectTypeBond } from "../../shared/components/formulario/select/SelectTypeBond";
import Input from "../../shared/components/formulario/input/input";
import { InputSwitch } from "../../shared/components/formulario/input-switch/switch";

export function EditarFuncionario() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setLoading } = useLoadingStore();

  const methods = useForm<EditarFuncionarioFormData>({
    resolver: yupResolver(editarFuncionarioSchema),
    defaultValues: {
      active: true,
      isResponsible: false,
      sector: SectorEnum.RH,
      typeBond: TypeBondEnum.CLT,
      modality: UnitOfMeasureEnum.SQUARE_METER,
    },
  });

  const {
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
  } = methods;

  const [valueOpcao, setOpicao] = useState<"cpf" | "cnpj">("cpf");

  useEffect(() => {
    if (id) {
      setLoading(true, "Carregando dados do funcionário...");
      funcionarioService
        .detalhar(id)
        .then((funcionario) => {
          reset({
            ...funcionario,
            dateOfBirth: formatDate(funcionario.dateOfBirth),
            admissionDate: formatDate(funcionario.admissionDate),
          });
          setOpicao(funcionario.cpf ? "cpf" : "cnpj");
        })
        .catch((err) => toastError(err))
        .finally(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    if (valueOpcao !== "cpf") limpaCamposPsJuridica({ methods });
    else limpaCamposPsFisica({ methods });
  }, [valueOpcao]);

  const sector = watch("sector");
  const idWork = watch("idWork");
  const active = watch("active");

  console.log("idWork:", idWork);

  const { mutate } = useMutation({
    mutationFn: (data: EditarFuncionarioFormData) =>
      funcionarioService.editar(id!, data),
    onSuccess: () => {
      toast.success("Funcionário editado com sucesso!");
      navigate("/colaboradores", { state: { reload: true } });
    },
    onError: (err: any) => toastError(err),
  });

  const setorSelecionado = useSectorById(sector);

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  const preencherCampos = (dados: any) => {
    setValue("corporateName", dados.razaoSocial || "");
    setValue("fantasyName", dados.fantasia || dados.razaoSocial || "");
  };

  useEffect(() => {
    setValue("idWork", "");
  }, [sector]);

  return (
    <BaseLayout title="Editar Colaborador">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => {
          mutate(formData);
        })}
        aoClicarBotaoVoltar={() => navigate("/colaboradores")}
      />

      <Box
        component="form"
        onSubmit={handleSubmit((formData) => {
          mutate(formData);
        })}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px)"
        gap="20px"
      >
        {/* CPF/CNPJ */}
        <Box display="flex" flexDirection="column" gap="20px">
          <FormControl>
            <FormLabel>Selecione CPF ou CNPJ</FormLabel>
            <RadioGroup
              value={valueOpcao}
              onChange={(e) => setOpicao(e.target.value as any)}
              row
            >
              <FormControlLabel value="cnpj" control={<Radio />} label="CNPJ" />
              <FormControlLabel value="cpf" control={<Radio />} label="CPF" />
            </RadioGroup>
          </FormControl>

          {valueOpcao === "cnpj" ? (
            <PessoaJuridicaFormPart
              methods={methods}
              mdDown={mdDown}
              preencherCampos={preencherCampos}
            />
          ) : (
            <PessoaFisicaFormPart methods={methods} mdDown={mdDown} />
          )}
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr 1fr" : "1fr 1fr"}
          gap="20px"
          width="100%"
        >
          <SelectTypeBond name="typeBond" methods={methods} />
          <InputSetores
            name="sector"
            label="Selecione um setor de Trabalho"
            methods={methods}
          />
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
          gap="20px"
        >
          <InputData
            name="dateOfBirth"
            label="Data de Nascimento"
            methods={methods}
          />

          <SelectUnitOfMeasure label="Modelo de Trabalho" name={`modality`} control={control} />
        </Box>
        {setorSelecionado?.origin === SectorEnum.WORK && (
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
            gap="20px"
          >
            <Controller
              name="idWork"
              control={control}
              render={({ field }) => (
                <SelectObra
                  value={field.value ?? ""}
                  onChange={field.onChange}
                />
              )}
            />

            {idWork && (
              <Controller
                name="positionId"
                control={control}
                render={({ field }) => (
                  <SelectCargo
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    idObra={idWork}
                  />
                )}
              />
            )}
          </Box>
        )}

        <ContaBancariaFormPart methods={methods} mdDown={mdDown} />

        <Box>
          <Typography variant="h5">Contato:</Typography>
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
            gap="20px"
          >
            <Input name="email" label="E-mail" methods={methods} type="email" />

            <InputMascarado
              name="phone"
              label="Telefone"
              mask={mascaraTelefone}
              methods={methods}
            />
          </Box>
        </Box>

        <EnderecoFormPart methods={methods} mdDown={mdDown} />

        <Box
          display="grid"
          gridTemplateColumns={
            mdDown ? "1fr 1fr" : !active ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr"
          }
          gap="20px"
        >
          <InputSwitch name="active" label="Funcionário ativo" methods={methods} />
          <InputData
            name="admissionDate"
            label="Data de Admissão"
            methods={methods}
          />
          {!active && (
            <InputData
              name="dateDismissal"
              label="Data de Desligamento"
              methods={methods}
            />
          )}
          {setorSelecionado?.origin === SectorEnum.WORK && (
            <InputSwitch
              name="isResponsible"
              label="Responsável pela Obra?"
              methods={methods}
            />
          )}
        </Box>
        <Typography variant="h5" padding="20px 0">
          Observações:
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
          gap="20px"
        >
          <Box sx={{ gridColumn: mdDown ? "auto" : "1 / -1" }}>
            <InputDescricao
              name="description"
              methods={methods}
              label="Descrição"
            />
          </Box>
          {!idWork && (
            <InputMascarado
              name="salary"
              methods={methods}
              label="Salário"
              mask={mascaraMoedaReal}
            />
          )}
        </Box>
      </Box>
    </BaseLayout>
  );
}
